import React, { useState, useEffect } from 'react';
import { Phone, Clock, Shield, Heart, Star } from 'lucide-react';
import { contactInfo } from '../mock';

const Hero = () => {
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show button after scrolling past 150px
      setShowButton(window.scrollY > 150);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToBooking = () => {
    const element = document.getElementById('booking');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToFlatrate = () => {
    const element = document.getElementById('flatrate');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    { icon: Clock, text: '24/7 Verfügbar' },
    { icon: Shield, text: 'Sicher & Zuverlässig' },
    { icon: Heart, text: 'Mit Liebe zum Tier' }
  ];

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Floating Pfotenflatrate Button - appears after scrolling */}
      {showButton && (
        <button
          onClick={scrollToFlatrate}
          className="fixed bottom-6 left-6 z-40 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-5 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center gap-2 animate-subtle-glow hover:animate-none"
          data-testid="floating-flatrate-btn"
        >
          <Star className="h-5 w-5 fill-current" />
          Zur Pfotenflatrate
        </button>
      )}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-orange-50 to-blue-50">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-orange-400 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-center lg:text-left space-y-8">
            <div className="space-y-4">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent block">Dom's</span>
                <span className="bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent block">Pfotenexpress</span>
              </h1>
              <p className="text-xl sm:text-2xl text-gray-700 font-medium">
                Ihr zuverlässiger Tier-Taxi Service in Hagen, Breckerfeld, Ennepetal, Gevelsberg, Herdecke, Schwelm, Wetter, Witten, Schwerte & Iserlohn
              </p>
              <p className="text-lg text-gray-600">
                Wir bringen Ihre liebsten Vierbeiner sicher und komfortabel ans Ziel – vom Tierarzt bis zur Tierklinik.
              </p>
            </div>

            <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-shadow duration-300"
                  >
                    <Icon className="h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">{feature.text}</span>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button
                onClick={scrollToBooking}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                Jetzt Termin buchen
              </button>
              <a
                href={`tel:${contactInfo.phone}`}
                className="flex items-center justify-center space-x-2 bg-white border-2 border-orange-500 text-orange-600 px-8 py-4 rounded-xl font-semibold text-lg shadow-md hover:bg-orange-50 hover:shadow-lg transition-all duration-300"
              >
                <Phone className="h-5 w-5" />
                <span>Jetzt anrufen</span>
              </a>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Phone className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">24/7 Notfall-Service</h3>
                  <p className="mt-1 text-sm text-red-700">
                    Im Notfall erreichen Sie uns rund um die Uhr unter{' '}
                    <a href={`tel:${contactInfo.phone}`} className="font-bold underline">
                      {contactInfo.phone}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative lg:block hidden">
            <div className="relative w-full h-[600px] rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
              <img
                src="https://customer-assets.emergentagent.com/job_585eba0e-1f9e-449c-b220-127f874d26c2/artifacts/92hbqy58_Screenshot_20251208-144336.png"
                alt="Dom's Pfotenexpress Fahrzeug"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600">24/7</div>
                <div className="text-sm text-gray-600 font-medium">Notdienst</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
